import { useQuery } from '@apollo/client';
import useUploadDialogActions from 'FeatureModules/Document/hooks/useUploadDialogActions';
import { IMAGE_FILE_TYPES } from 'Components/FileUpload/Dropzone';
import { EFilterType, TFilter, TFilterEvent } from 'Components/Filter/filter.types';
import useDialog from 'hooks/useDialog';
import { useLogEvent } from 'hooks/useLogEvent';
import { DOCUMENTS_PER_PAGE } from 'utils/pagination.utils';
import { GET_SHARED_DOCUMENTS } from 'graphql/secure/queries';
import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { EDocumentCategory, IBaseDocument } from 'FeatureModules/Document/types/document.types';
import { IGraphQlConnection } from 'types/core.types';
import {
  ArrayParam,
  createEnumArrayParam,
  ObjectParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { removeGraphConnections } from 'utils/graphql.utils';

const DATE_FORMAT = 'YYYY-MM-DD';

const usePartnerDocuments = () => {
  const { id } = useParams<{ id: string | undefined }>();
  const { openDialog } = useDialog();
  const { handleDeleteDocuments } = useUploadDialogActions();
  const { logEvent } = useLogEvent();

  const [query, setQuery] = useQueryParams(
    {
      searchTerm: withDefault(StringParam, ''),
      creationDateRange: ObjectParam,
      categories: withDefault(createEnumArrayParam(Object.keys(EDocumentCategory)), null),
      siteIds: withDefault(ArrayParam, undefined),
    },
    { removeDefaultsFromUrl: true }
  );

  const { searchTerm, creationDateRange, categories, siteIds } = query;

  const { data, loading, error, fetchMore } = useQuery<{
    sharedDocuments: IGraphQlConnection<IBaseDocument>;
  }>(GET_SHARED_DOCUMENTS, {
    variables: {
      first: DOCUMENTS_PER_PAGE,
      filters: {
        fileExtensions: [
          '.pdf',
          // Workaround since we currently do not want to show the other files from the imports (geojson, csv, etc.)
          // TODO: Add proper file type handling in the future and show all files in our app
          ...IMAGE_FILE_TYPES,
        ],
        createdByIds: [id],
        ownedByIds: [id],
      },
    },
  });

  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);

  const documents: IBaseDocument[] = useMemo(
    () => (data?.sharedDocuments ? removeGraphConnections(data?.sharedDocuments) : []),
    [data]
  );

  const filters: TFilter[] = useMemo(
    () => [
      {
        type: EFilterType.DOCUMENT_CATEGORY,
        value: categories,
      },
      {
        type: EFilterType.SITES,
        value: (siteIds?.filter(Boolean) || undefined) as string[] | undefined,
      },
      {
        type: EFilterType.TIME_FRAME,
        label: 'Upload date',
        value:
          creationDateRange?.startDate || creationDateRange?.endDate
            ? {
                startDate: creationDateRange?.startDate
                  ? new Date(creationDateRange.startDate)
                  : undefined,
                endDate: creationDateRange?.endDate
                  ? new Date(creationDateRange.endDate)
                  : undefined,
              }
            : null,
      },
    ],
    [categories, creationDateRange, siteIds]
  );

  const hasNextPage: boolean =
    !!data?.sharedDocuments?.pageInfo?.hasNextPage &&
    data?.sharedDocuments?.edges?.length === documents.length;
  const endCursor: string | undefined = data?.sharedDocuments?.pageInfo?.endCursor;

  const handlePageEndReached = useCallback(() => {
    if (endCursor) {
      fetchMore({
        variables: {
          after: endCursor,
        },
      });
    }
  }, [endCursor, fetchMore]);

  const handleDelete = async () => {
    await handleDeleteDocuments({
      variables: {
        ids: setSelectedDocumentIds,
      },
    });
    if (hasNextPage && endCursor) {
      await fetchMore({
        variables: {
          first: setSelectedDocumentIds.length,
          after: endCursor,
        },
      });
    }
    setSelectedDocumentIds([]);
  };

  const handleDeleteSelectedDocuments = () => {
    openDialog({
      type: 'ALERT',
      props: {
        displayCloseButton: true,
        title: `Delete ${selectedDocumentIds.length === 1 ? 'document' : 'documents'}`,
        submitText: 'Delete',
        itemTitle: selectedDocumentIds.length === 1 ? '' : `${selectedDocumentIds.length} selected`,
        text:
          selectedDocumentIds.length === 1
            ? 'Are you sure you want to delete this document? You will not be able to restore it.'
            : 'Are you sure you want to delete these documents? You will not be able to restore them.',
        onSubmit: handleDelete,
      },
    });
  };

  const handleCreateDocument = () => {
    openDialog({
      type: 'UPLOAD_DOCUMENTS',
      props: {
        defaultOwnedById: id,
      },
    });
  };

  const allowBulkDeletion = useMemo(() => {
    const selectedDocuments = documents.filter(({ id }) => selectedDocumentIds.includes(id));
    return (
      selectedDocuments.reduce((current, { usageCount }) => current + (usageCount || 0), 0) === 0
    );
  }, [documents, selectedDocumentIds]);

  const handleFilterChange = (event: TFilterEvent) => {
    switch (event.type) {
      case EFilterType.TIME_FRAME: {
        const { startDate, endDate } = event.value || {};

        setQuery({
          creationDateRange: {
            startDate: startDate ? moment(startDate).format(DATE_FORMAT) : undefined,
            endDate: endDate ? moment(endDate).format(DATE_FORMAT) : undefined,
          },
        });
        break;
      }
      case EFilterType.DOCUMENT_CATEGORY: {
        setQuery({
          ...event,
          categories: event.value,
        });
        break;
      }
      case EFilterType.SITES: {
        setQuery({
          ...event,
          siteIds: event.value || undefined,
        });
        break;
      }
      default:
        break;
    }
    logEvent('INTERNAL_DOCUMENTS_FILTERED', { type: event.type });
  };

  const handleFilterReset = () => {
    setQuery({
      searchTerm: '',
      creationDateRange: undefined,
      categories: undefined,
      siteIds: undefined,
    });
  };

  const handleSearchTermChange = (searchTerm: string) => {
    setQuery({ searchTerm });
    logEvent('INTERNAL_DOCUMENTS_SEARCHED', { searchTerm });
  };

  return {
    error,
    selectedDocumentIds,
    loading,
    documents,
    searchTerm,
    endCursor,
    hasNextPage,
    allowBulkDeletion,
    filters,
    handleFilterChange,
    handleFilterReset,
    handleSearchTermChange,
    handlePageEndReached,
    setSelectedDocumentIds,
    handleDeleteSelectedDocuments,
    handleCreateDocument,
  };
};

export default usePartnerDocuments;
